import React from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Link,
  Navigate,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  BottomNavigation,
  BottomNavigationAction,
  Divider,
  Stack,
  Paper,
  alpha,
} from '@mui/material';
import type { Theme } from '@mui/material/styles';
import {
  Dashboard as DashboardIcon,
  Schedule as ScheduleIcon,
  VideoLibrary as VideoIcon,
  PlaylistAdd as AdIcon,
  Menu as MenuIcon,
  Build as BuildIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  AssignmentTurnedIn as AssignmentTurnedInIcon,
  BarChart as BarChartIcon,
  Business as BusinessIcon,
  Description as DescriptionIcon,
  Campaign as CampaignIcon,
  MoreHoriz as MoreHorizIcon,
  Close as CloseIcon,
  Settings as SettingsIcon,
  Groups as GroupsIcon,
  Mail as MailIcon,
  ReceiptLong as ReceiptLongIcon,
  AutoStories as AutoStoriesIcon,
} from '@mui/icons-material';
import theme from './theme';
import Dashboard from './pages/Dashboard';
import TaskManagement from './pages/TaskManagement';
import DataView from './pages/DataView';
import SystemPage from './pages/SystemPage';
import ProcessHistory from './pages/ProcessHistory';
import VideoAdManager from './pages/VideoAdManager';
import OrdVkPage from './pages/OrdVkPage';
import PersonsPage from './pages/cabinet/PersonsPage';
import ContractsPage from './pages/cabinet/ContractsPage';
import CreativesPage from './pages/cabinet/CreativesPage';
import ClientsPage from './pages/cabinet/ClientsPage';
import SettingsPage from './pages/cabinet/SettingsPage';
import ClientHomePage from './pages/cabinet/ClientHomePage';
import LoginPage from './pages/LoginPage';
import LandingPage from './pages/LandingPage';
import ConnectionRequestsPage from './pages/cabinet/ConnectionRequestsPage';
import InvoicesPage from './pages/cabinet/InvoicesPage';
import DzenReportPage from './pages/cabinet/DzenReportPage';
import PlatformsPage from './pages/cabinet/PlatformsPage';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { CircularProgress, Button } from '@mui/material';
import { Logout as LogoutIcon, Storefront as StorefrontIcon } from '@mui/icons-material';

const drawerWidth = 232;
const collapsedWidth = 68;

type MenuItem = {
  text: string;
  short?: string;
  icon: React.ReactNode;
  path: string;
  group?: 'cabinet' | 'ops';
};

const menuItems: MenuItem[] = [
  { text: 'Главная', short: 'Главная', icon: <DashboardIcon fontSize="small" />, path: '/', group: 'ops' },
  { text: 'Креативы', short: 'Креативы', icon: <CampaignIcon fontSize="small" />, path: '/cabinet/creatives', group: 'cabinet' },
  { text: 'Контрагенты', short: 'Контр.', icon: <BusinessIcon fontSize="small" />, path: '/cabinet/persons', group: 'cabinet' },
  { text: 'Площадки', short: 'Площадки', icon: <StorefrontIcon fontSize="small" />, path: '/cabinet/platforms', group: 'cabinet' },
  { text: 'Договоры', short: 'Договоры', icon: <DescriptionIcon fontSize="small" />, path: '/cabinet/contracts', group: 'cabinet' },
  { text: 'Управление задачами', short: 'Задачи', icon: <AssignmentTurnedInIcon fontSize="small" />, path: '/tasks', group: 'ops' },
  { text: 'История запусков', short: 'История', icon: <ScheduleIcon fontSize="small" />, path: '/process-history', group: 'ops' },
  { text: 'Просмотр данных', short: 'Данные', icon: <VideoIcon fontSize="small" />, path: '/data', group: 'ops' },
  { text: 'Менеджер рекламы', short: 'Реклама', icon: <AdIcon fontSize="small" />, path: '/ads', group: 'ops' },
  { text: 'Система', short: 'Система', icon: <BuildIcon fontSize="small" />, path: '/system', group: 'ops' },
  { text: 'ОРД VK', short: 'ОРД', icon: <BarChartIcon fontSize="small" />, path: '/ord-vk', group: 'ops' },
];

const ownerCabinetExtras: MenuItem[] = [
  { text: 'Клиенты ТрендиОРД', short: 'Клиенты', icon: <GroupsIcon fontSize="small" />, path: '/cabinet/clients', group: 'cabinet' },
  { text: 'Заявки на подключение', short: 'Заявки', icon: <MailIcon fontSize="small" />, path: '/cabinet/connection-requests', group: 'cabinet' },
];

const clientAdminMenuItems: MenuItem[] = [
  { text: 'Главная', short: 'Главная', icon: <DashboardIcon fontSize="small" />, path: '/cabinet/home', group: 'cabinet' },
  { text: 'Креативы', short: 'Креативы', icon: <CampaignIcon fontSize="small" />, path: '/cabinet/creatives', group: 'cabinet' },
  { text: 'Контрагенты', short: 'Контр.', icon: <BusinessIcon fontSize="small" />, path: '/cabinet/persons', group: 'cabinet' },
  { text: 'Площадки', short: 'Площадки', icon: <StorefrontIcon fontSize="small" />, path: '/cabinet/platforms', group: 'cabinet' },
  { text: 'Договоры', short: 'Договоры', icon: <DescriptionIcon fontSize="small" />, path: '/cabinet/contracts', group: 'cabinet' },
  { text: 'Акты', short: 'Акты', icon: <ReceiptLongIcon fontSize="small" />, path: '/cabinet/invoices', group: 'cabinet' },
  { text: 'Дзен', short: 'Дзен', icon: <AutoStoriesIcon fontSize="small" />, path: '/cabinet/dzen', group: 'cabinet' },
  { text: 'Настройки', short: 'Настр.', icon: <SettingsIcon fontSize="small" />, path: '/cabinet/settings', group: 'cabinet' },
];

const dzenMenuItem: MenuItem = {
  text: 'Дзен',
  short: 'Дзен',
  icon: <AutoStoriesIcon fontSize="small" />,
  path: '/cabinet/dzen',
  group: 'cabinet',
};

function buildLimitedMenu(
  canCreatives: boolean,
  canPersons: boolean,
  canContracts: boolean,
  canInvoices: boolean,
  includeHome: boolean,
): MenuItem[] {
  const items: MenuItem[] = [];
  if (includeHome) {
    items.push({ text: 'Главная', short: 'Главная', icon: <DashboardIcon fontSize="small" />, path: '/cabinet/home', group: 'cabinet' });
  }
  if (canCreatives) {
    items.push({ text: 'Креативы', short: 'Креативы', icon: <CampaignIcon fontSize="small" />, path: '/cabinet/creatives', group: 'cabinet' });
  }
  if (canPersons) {
    items.push({ text: 'Контрагенты', short: 'Контр.', icon: <BusinessIcon fontSize="small" />, path: '/cabinet/persons', group: 'cabinet' });
    items.push({ text: 'Площадки', short: 'Площадки', icon: <StorefrontIcon fontSize="small" />, path: '/cabinet/platforms', group: 'cabinet' });
  }
  if (canContracts) {
    items.push({ text: 'Договоры', short: 'Договоры', icon: <DescriptionIcon fontSize="small" />, path: '/cabinet/contracts', group: 'cabinet' });
  }
  if (canInvoices) {
    items.push({ text: 'Акты', short: 'Акты', icon: <ReceiptLongIcon fontSize="small" />, path: '/cabinet/invoices', group: 'cabinet' });
  }
  return items;
}

const clientAdminPaths = clientAdminMenuItems.map((i) => i.path);

const mobilePrimary = [
  menuItems[0],
  menuItems[1],
  menuItems[2],
  menuItems[3],
];

function NavList({
  open,
  onNavigate,
  items,
}: {
  open: boolean;
  onNavigate?: () => void;
  items: MenuItem[];
}) {
  const location = useLocation();

  const renderGroup = (title: string, groupItems: MenuItem[]) => (
    <Box key={title} sx={{ mb: 1 }}>
      {open && (
        <Typography
          variant="caption"
          sx={{
            px: 2.5,
            pt: 1,
            pb: 0.5,
            display: 'block',
            color: 'text.secondary',
            fontWeight: 700,
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
          }}
        >
          {title}
        </Typography>
      )}
      {groupItems.map((item) => {
        const selected = location.pathname === item.path;
        return (
          <ListItemButton
            key={item.path}
            component={Link}
            to={item.path}
            selected={selected}
            onClick={onNavigate}
            sx={{ justifyContent: open ? 'flex-start' : 'center', px: open ? 1.5 : 1 }}
          >
            <ListItemIcon
              sx={{
                minWidth: 0,
                mr: open ? 1.5 : 0,
                justifyContent: 'center',
                color: selected ? 'primary.main' : 'text.secondary',
              }}
            >
              {item.icon}
            </ListItemIcon>
            {open && <ListItemText primary={item.text} primaryTypographyProps={{ fontSize: '0.88rem', fontWeight: selected ? 700 : 600 }} />}
          </ListItemButton>
        );
      })}
    </Box>
  );

  return (
    <List disablePadding sx={{ py: 0.5 }}>
      {renderGroup('Кабинет', items.filter((i) => i.group === 'cabinet'))}
      {items.some((i) => i.group === 'ops') && (
        <>
          <Divider sx={{ mx: 2, my: 0.5 }} />
          {renderGroup('Операции', items.filter((i) => i.group === 'ops'))}
        </>
      )}
    </List>
  );
}

function AppShell() {
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [moreOpen, setMoreOpen] = React.useState(false);
  const [desktopOpen, setDesktopOpen] = React.useState(true);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, loading, isOwner, isManager, isStaff, isClientAdmin, canCreatives, canPersons, canContracts, canInvoices, logout } = useAuth();

  const visibleMenu = React.useMemo(() => {
    const dzenOn = !!user?.dzenSubmissionEnabled;
    if (isClientAdmin) {
      return dzenOn
        ? clientAdminMenuItems
        : clientAdminMenuItems.filter((i) => i.path !== '/cabinet/dzen');
    }
    if (isStaff) {
      const base = buildLimitedMenu(canCreatives, canPersons, canContracts, canInvoices, false);
      return dzenOn && canCreatives ? [...base.slice(0, -1), dzenMenuItem, ...base.slice(-1)] : base;
    }
    if (isManager) {
      const base = buildLimitedMenu(canCreatives, canPersons, canContracts, canInvoices, false);
      return dzenOn && canCreatives ? [...base, dzenMenuItem] : base;
    }
    if (isOwner) {
      const cabinet = menuItems.filter((i) => i.group === 'cabinet');
      const ops = menuItems.filter((i) => i.group === 'ops');
      const invoices: MenuItem = { text: 'Акты', short: 'Акты', icon: <ReceiptLongIcon fontSize="small" />, path: '/cabinet/invoices', group: 'cabinet' };
      const withDzen = dzenOn ? [dzenMenuItem] : [];
      return [...cabinet, invoices, ...withDzen, ...ownerCabinetExtras, ...ops];
    }
    return menuItems;
  }, [isClientAdmin, isStaff, isManager, isOwner, canCreatives, canPersons, canContracts, canInvoices, user?.dzenSubmissionEnabled]);
  const visibleMobilePrimary = React.useMemo(
    () => visibleMenu.filter((i) => i.group === 'cabinet').slice(0, 3),
    [visibleMenu]
  );

  React.useEffect(() => {
    if (!user) return;
    const allowedPaths = visibleMenu.map((i) => i.path);
    if (isClientAdmin) {
      if (!clientAdminPaths.includes(location.pathname) && location.pathname !== '/login') {
        navigate('/cabinet/home', { replace: true });
      }
      return;
    }
    if (isStaff || isManager) {
      if (
        allowedPaths.length > 0
        && !allowedPaths.includes(location.pathname)
        && location.pathname !== '/login'
      ) {
        navigate(allowedPaths[0], { replace: true });
      }
    }
  }, [isClientAdmin, isStaff, isManager, user, location.pathname, navigate, visibleMenu]);

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!user) {
    return (
      <Box
        sx={{
          height: '100dvh',
          overflowY: 'auto',
          overflowX: 'hidden',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Box>
    );
  }

  const drawerWidthActive = desktopOpen ? drawerWidth : collapsedWidth;
  const primaryPaths = visibleMobilePrimary.map((i) => i.path);
  const bottomValue = primaryPaths.includes(location.pathname) ? location.pathname : 'more';

  const brand = (
    <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
      <Box
        sx={{
          width: 28,
          height: 28,
          borderRadius: '9px',
          background: 'linear-gradient(135deg, #0F766E 0%, #0891B2 100%)',
          boxShadow: '0 4px 12px rgba(15, 118, 110, 0.28)',
          flexShrink: 0,
        }}
      />
      <Box sx={{ minWidth: 0 }}>
        <Typography
          variant="subtitle1"
          noWrap
          sx={{ fontWeight: 800, letterSpacing: '-0.03em', lineHeight: 1.1 }}
        >
          TrendyORD
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          noWrap
          sx={{ display: { xs: 'none', md: 'block' }, lineHeight: 1.1 }}
        >
          Статистика и кабинет ОРД
        </Typography>
      </Box>
    </Stack>
  );

  const drawerBody = (open: boolean, onNavigate?: () => void) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Toolbar sx={{ gap: 1, px: open ? 1.5 : 1 }}>
        {open ? brand : (
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: '9px',
              background: 'linear-gradient(135deg, #0F766E 0%, #0891B2 100%)',
              mx: 'auto',
            }}
          />
        )}
      </Toolbar>
      <Divider />
      <Box sx={{ flex: 1, overflowY: 'auto', py: 0.5 }}>
        <NavList open={open} onNavigate={onNavigate} items={visibleMenu} />
      </Box>
      {open && (
        <Box sx={{ px: 1.5, pb: 1 }}>
          <Typography variant="caption" color="text.secondary" display="block" noWrap>
            {isOwner
              ? 'Владелец'
              : isClientAdmin
                ? `Клиент · ${user.ordClientName || user.login}`
                : `Менеджер · ${user.inn || ''}`}
          </Typography>
          <Button
            size="small"
            startIcon={<LogoutIcon />}
            onClick={() => logout().then(() => navigate('/login'))}
            sx={{ mt: 0.5 }}
          >
            Выйти ({user.login})
          </Button>
        </Box>
      )}
      <Box sx={{ p: 1, display: { xs: 'none', sm: 'flex' }, justifyContent: open ? 'flex-end' : 'center' }}>
        <IconButton
          onClick={() => setDesktopOpen((v) => !v)}
          size="small"
          sx={{
            bgcolor: (t) => alpha(t.palette.primary.main, 0.08),
            '&:hover': { bgcolor: (t) => alpha(t.palette.primary.main, 0.14) },
          }}
        >
          {desktopOpen ? <ChevronLeftIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
        </IconButton>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', height: '100dvh', maxWidth: '100%', overflow: 'hidden' }}>
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidthActive}px)` },
          ml: { sm: `${drawerWidthActive}px` },
          maxWidth: '100%',
          transition: 'width 0.25s ease, margin-left 0.25s ease',
        }}
      >
        <Toolbar sx={{ gap: 1, minHeight: { xs: 52, sm: 56 }, px: { xs: 1, sm: 2 } }}>
          <IconButton
            aria-label="open drawer"
            edge="start"
            onClick={() => setMobileOpen(true)}
            sx={{ display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Box sx={{ display: { xs: 'block', sm: 'none' }, flex: 1, minWidth: 0, overflow: 'hidden' }}>{brand}</Box>
          <Typography
            variant="body2"
            color="text.secondary"
            noWrap
            sx={{ display: { xs: 'none', sm: 'block' }, flex: 1, minWidth: 0 }}
          >
            {visibleMenu.find((i) => i.path === location.pathname)?.text || 'Кабинет'}
          </Typography>
        </Toolbar>
      </AppBar>

      <Box
        component="nav"
        sx={{ width: { sm: drawerWidthActive }, flexShrink: { sm: 0 }, transition: 'width 0.25s ease' }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          transitionDuration={{ enter: 160, exit: 120 }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              display: 'flex',
              flexDirection: 'column',
            },
          }}
        >
          <Stack direction="row" alignItems="center" sx={{ px: 1.5, pt: 1.25, pb: 0.5 }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>{brand}</Box>
            <IconButton onClick={() => setMobileOpen(false)} size="small">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
          <Divider />
          <Box sx={{ flex: 1, overflowY: 'auto', py: 0.5 }}>
            <NavList open onNavigate={() => setMobileOpen(false)} items={visibleMenu} />
          </Box>
          <Divider />
          <Box sx={{ px: 1.5, py: 1.25 }}>
            <Typography variant="caption" color="text.secondary" display="block" noWrap>
              {isOwner
                ? 'Владелец'
                : isClientAdmin
                  ? `Клиент · ${user.ordClientName || user.login}`
                  : `Менеджер · ${user.inn || ''}`}
            </Typography>
            <Button
              size="small"
              startIcon={<LogoutIcon />}
              onClick={() => {
                setMobileOpen(false);
                logout().then(() => navigate('/login'));
              }}
              sx={{ mt: 0.5 }}
            >
              Выйти ({user.login})
            </Button>
          </Box>
        </Drawer>

        <Drawer
          variant="permanent"
          open={desktopOpen}
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidthActive,
              transition: 'width 0.25s ease',
              overflowX: 'hidden',
            },
          }}
        >
          {drawerBody(desktopOpen)}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minWidth: 0,
          minHeight: 0,
          width: { xs: '100%', sm: `calc(100% - ${drawerWidthActive}px)` },
          maxWidth: '100%',
          overflowY: 'auto',
          overflowX: 'hidden',
          WebkitOverflowScrolling: 'touch',
          transition: 'width 0.25s ease',
          pb: {
            xs: 'calc(72px + env(safe-area-inset-bottom, 0px))',
            sm: 2,
          },
          px: { xs: 1.25, sm: 2, md: 2.5 },
          pt: 0,
        }}
      >
        <Toolbar />
        <Box sx={{ py: { xs: 1.25, sm: 1.5 } }}>
          <Routes>
            <Route
              path="/"
              element={
                isClientAdmin ? (
                  <Navigate to="/cabinet/home" replace />
                ) : isStaff || isManager ? (
                  <Navigate to={canCreatives ? '/cabinet/creatives' : canContracts ? '/cabinet/contracts' : canInvoices ? '/cabinet/invoices' : '/cabinet/persons'} replace />
                ) : (
                  <Dashboard />
                )
              }
            />
            <Route path="/cabinet/home" element={isClientAdmin ? <ClientHomePage /> : <Navigate to="/" replace />} />
            <Route path="/cabinet/settings" element={isClientAdmin ? <SettingsPage /> : <Navigate to="/" replace />} />
            <Route
              path="/cabinet/dzen"
              element={
                user?.dzenSubmissionEnabled ? <DzenReportPage /> : <Navigate to={isClientAdmin ? '/cabinet/settings' : '/'} replace />
              }
            />
            <Route path="/cabinet/clients" element={isOwner ? <ClientsPage /> : <Navigate to="/" replace />} />
            <Route
              path="/cabinet/connection-requests"
              element={isOwner ? <ConnectionRequestsPage /> : <Navigate to="/" replace />}
            />
            <Route
              path="/cabinet/persons"
              element={
                !canPersons && (isManager || isStaff) ? <Navigate to={visibleMenu[0]?.path || '/'} replace /> : <PersonsPage />
              }
            />
            <Route
              path="/cabinet/platforms"
              element={
                !canPersons && (isManager || isStaff) ? <Navigate to={visibleMenu[0]?.path || '/'} replace /> : <PlatformsPage />
              }
            />
            <Route
              path="/cabinet/contracts"
              element={
                !canContracts && (isManager || isStaff) ? <Navigate to={visibleMenu[0]?.path || '/'} replace /> : <ContractsPage />
              }
            />
            <Route
              path="/cabinet/invoices"
              element={
                !canInvoices && (isManager || isStaff) ? <Navigate to={visibleMenu[0]?.path || '/'} replace /> : <InvoicesPage />
              }
            />
            <Route
              path="/cabinet/creatives"
              element={
                !canCreatives && (isManager || isStaff) ? <Navigate to={visibleMenu[0]?.path || '/'} replace /> : <CreativesPage />
              }
            />
            <Route path="/tasks" element={isOwner ? <TaskManagement /> : <Navigate to={isClientAdmin ? '/cabinet/home' : '/cabinet/creatives'} replace />} />
            <Route path="/process-history" element={isOwner ? <ProcessHistory /> : <Navigate to={isClientAdmin ? '/cabinet/home' : '/cabinet/creatives'} replace />} />
            <Route path="/data" element={isOwner ? <DataView /> : <Navigate to={isClientAdmin ? '/cabinet/home' : '/cabinet/creatives'} replace />} />
            <Route path="/data-view" element={isOwner ? <DataView /> : <Navigate to={isClientAdmin ? '/cabinet/home' : '/cabinet/creatives'} replace />} />
            <Route path="/ads" element={isOwner ? <VideoAdManager /> : <Navigate to={isClientAdmin ? '/cabinet/home' : '/cabinet/creatives'} replace />} />
            <Route path="/system" element={isOwner ? <SystemPage /> : <Navigate to={isClientAdmin ? '/cabinet/home' : '/cabinet/creatives'} replace />} />
            <Route path="/ord-vk" element={isOwner ? <OrdVkPage /> : <Navigate to={isClientAdmin ? '/cabinet/home' : '/cabinet/creatives'} replace />} />
          </Routes>
        </Box>
      </Box>

      {/* Mobile bottom navigation */}
      <Paper
        elevation={0}
        sx={{
          display: { xs: 'block', sm: 'none' },
          position: 'fixed',
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: (t: Theme) => t.zIndex.appBar,
          borderRadius: 0,
          borderLeft: 0,
          borderRight: 0,
          borderBottom: 0,
          pb: 'env(safe-area-inset-bottom)',
        }}
      >
        <BottomNavigation
          showLabels
          value={bottomValue}
          sx={{
            height: 56,
            '& .MuiBottomNavigationAction-root': {
              minWidth: 0,
              maxWidth: 'none',
              px: 0.5,
              flex: 1,
            },
            '& .MuiBottomNavigationAction-label': {
              fontSize: '0.65rem',
              lineHeight: 1.15,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxWidth: '100%',
            },
          }}
          onChange={(_, value) => {
            if (value === 'more') {
              setMoreOpen(true);
              return;
            }
            navigate(value);
          }}
        >
          {visibleMobilePrimary.map((item) => (
            <BottomNavigationAction
              key={item.path}
              label={item.short}
              value={item.path}
              icon={item.icon}
            />
          ))}
          {(isOwner || isClientAdmin || isStaff || isManager) && (
            <BottomNavigationAction label="Ещё" value="more" icon={<MoreHorizIcon fontSize="small" />} />
          )}
        </BottomNavigation>
      </Paper>

      <Drawer
        anchor="bottom"
        open={moreOpen}
        onClose={() => setMoreOpen(false)}
        transitionDuration={{ enter: 160, exit: 120 }}
        PaperProps={{
          sx: {
            borderTopLeftRadius: 20,
            borderTopRightRadius: 20,
            maxHeight: '70vh',
            pb: 'env(safe-area-inset-bottom)',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        <Box sx={{ px: 2, pt: 1.5, pb: 0.5 }}>
          <Box sx={{ width: 36, height: 4, borderRadius: 2, bgcolor: 'divider', mx: 'auto', mb: 1.5 }} />
          <Typography variant="subtitle1" sx={{ mb: 0.5 }}>Ещё разделы</Typography>
        </Box>
        <Box sx={{ flex: 1, overflowY: 'auto' }}>
          <NavList open onNavigate={() => setMoreOpen(false)} items={visibleMenu} />
        </Box>
        <Divider />
        <Box sx={{ px: 2, py: 1.25 }}>
          <Button
            size="small"
            startIcon={<LogoutIcon />}
            onClick={() => {
              setMoreOpen(false);
              logout().then(() => navigate('/login'));
            }}
          >
            Выйти ({user.login})
          </Button>
        </Box>
      </Drawer>
    </Box>
  );
}

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router basename={process.env.PUBLIC_URL || '/ord'}>
        <AuthProvider>
          <AppShell />
        </AuthProvider>
      </Router>
    </ThemeProvider>
  );
}

export default App;
